import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText } from './common.js'

/**
 * A calendar note about a worker (or the whole workshop): showed up late,
 * didn't come in, is on leave, did overtime, or anything else worth a
 * date-stamped record — a broken machine, a public holiday, a delivery that
 * never arrived.
 */
export const STAFF_EVENT_TYPES = ['late', 'absent', 'holiday', 'overtime', 'other'] as const
export type StaffEventType = (typeof STAFF_EVENT_TYPES)[number]
export const staffEventTypeSchema = z.enum(STAFF_EVENT_TYPES)

export const STAFF_EVENT_TYPE_LABELS: Record<StaffEventType, string> = {
  late: 'Late',
  absent: "Didn't come in",
  holiday: 'On leave',
  overtime: 'Overtime',
  other: 'Other',
}

const staffEventFields = {
  /** Null for a general event that isn't about one specific worker. */
  workerId: idSchema.nullable(),
  type: staffEventTypeSchema,
  /** First day this covers, e.g. "2026-09-05". */
  date: z.string().min(1),
  /**
   * Last day this covers, inclusive — the same as `date` for a single day.
   * Only 'holiday' exposes this as a range in the UI; every other type just
   * sends it equal to `date`.
   */
  endDate: z.string().min(1),
  /** Required for 'other'; optional context for the rest. Enforced below. */
  note: optionalText(1000),
  /**
   * "HH:mm" clock-in time — required for 'late', meaningless for every other
   * type. Drives the automatic salary-deduction calculation (see
   * @garage/shared's computeLateDeduction), so a 'late' event only ever
   * covers one day: a single arrival time cannot describe a range of days.
   */
  arrivalTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm')
    .nullable()
    .default(null),
  /**
   * "HH:mm" clock-out time — required for 'overtime', meaningless for every
   * other type. Drives the automatic overtime-pay calculation (see
   * @garage/shared's computeOvertimePay), so an 'overtime' event only ever
   * covers one day: a single departure time cannot describe a range of days.
   */
  departureTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm')
    .nullable()
    .default(null),
}

function checkRefinements(
  value: {
    type: StaffEventType
    workerId: string | null
    date: string
    endDate: string
    note: string | null
    arrivalTime: string | null
    departureTime: string | null
  },
  ctx: z.RefinementCtx,
) {
  if (value.type === 'other' && !value.note) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['note'], message: 'Say what happened' })
  }
  // A holiday is time off for somebody specific — there is no "general" leave.
  if (value.type === 'holiday' && !value.workerId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['workerId'], message: 'Choose who is on leave' })
  }
  if (value.endDate < value.date) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'End date is before the start date' })
  }
  if (value.type === 'late') {
    if (!value.arrivalTime) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['arrivalTime'], message: 'What time did they arrive?' })
    }
    if (value.endDate !== value.date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'A late arrival is a single day',
      })
    }
  }
  if (value.type === 'overtime') {
    if (!value.departureTime) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['departureTime'], message: 'What time did they leave?' })
    }
    if (value.endDate !== value.date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Overtime is a single day',
      })
    }
  }
}

export const staffEventInputSchema = z.object(staffEventFields).superRefine(checkRefinements)
export type StaffEventInput = z.infer<typeof staffEventInputSchema>

export const staffEventSchema = z.object({
  ...staffEventFields,
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type StaffEvent = z.infer<typeof staffEventSchema>

export interface StaffEventWithWorker extends StaffEvent {
  /** Null for a general event. */
  workerName: string | null
}
